// src/lib/resources/benchmarks/data.ts

/**
 * @file Cited benchmark datasets
 * @description
 * Numeric, source-attributed benchmark bands consumed by the benchmark engine
 * (`./compare`). Figures are consolidated from the site's own cited glossary
 * `segmentBenchmarks` and benchmark blog posts so every surface reads one
 * typed source of truth instead of parsing prose ranges at runtime.
 *
 * Editing rule: every band keeps real `sources`. Where a number is derived
 * (not directly published) the `note` says so ("directional"). Refresh
 * alongside the underlying glossary/blog data.
 */

import type { BenchmarkSet, BenchmarkSegment } from './compare'
import {
  PAID_SOCIAL_CPM_BENCHMARK,
  PAID_SOCIAL_CPC_BENCHMARK,
  PAID_SOCIAL_CTR_BENCHMARK,
} from './paid-social'

const SITE = 'https://www.adsights.ai'

/** ThruPlay creative-length buckets (one axis of the tool's two selectors). */
export const THRUPLAY_LENGTHS = ['Under 15s', '15–30s', '30–60s'] as const
/** ThruPlay audience-temperature buckets (the other axis). */
export const THRUPLAY_AUDIENCES = ['Cold prospecting', 'Warm / retargeting'] as const

/** Hook-rate platform buckets. Each platform uses a different view threshold. */
export const HOOK_RATE_PLATFORMS = ['Meta', 'TikTok', 'YouTube Shorts'] as const
/** Meta hook rate is placement-segmented (Feed/Reels/Stories move the band
 *  materially); the analyzer exposes these only when the platform is Meta. */
export const HOOK_META_PLACEMENTS = ['Feed', 'Reels', 'Stories'] as const
/** Meta hook audience-temperature axis. Cold prospecting is placement-split;
 *  the cited warm/retargeting band is placement-agnostic. */
export const HOOK_META_AUDIENCES = ['Cold prospecting', 'Warm / retargeting'] as const

/**
 * ThruPlay rate (% of video plays that reach a ThruPlay — 15s or completion).
 * Higher is better. A TWO-AXIS dataset — creative LENGTH × audience
 * temperature — because length and audience move the number independently and
 * collapsing them into one picker is misleading. Each cell is matched to the
 * site's cited benchmark content:
 *   - Under 15s: glossary "Video under 15s" band (Nielsen completion data);
 *     mechanical, so audience barely moves it → resolved by length alone.
 *   - 15–30s cold: the benchmark guide's cited Affect Group 18–28% (also in
 *     the cross-platform post + the tool FAQ).
 *   - 15–30s warm: glossary retargeting band (25–45%).
 *   - 30–60s cold: glossary "Video 30–60s DTC prospecting" band (8–18%).
 *   - 30–60s warm: directional — warm uplift on the 30–60s cold band (no
 *     public 30–60s × warm range is cited).
 * Denominator follows the glossary definition: ThruPlays ÷ video plays (Meta's
 * reported basis; on Meta, autoplay keeps video plays ≈ impressions).
 */
export const THRUPLAY_RATE_BENCHMARK: BenchmarkSet = {
  id: 'thruplay-rate',
  metricLabel: 'ThruPlay Rate',
  unit: 'percent',
  higherIsBetter: true,
  glossaryUrl: `${SITE}/resources/glossary/metrics/thruplay-rate`,
  // Routed under the topic path; there is no /blog/[post] route.
  referenceUrl: `${SITE}/blog/topics/ad-performance/what-is-a-good-thruplay-rate-2026-benchmarks`,
  segments: [
    {
      id: 'under-15s',
      segment: 'Under 15s creative',
      length: 'Under 15s',
      // audience-independent (mechanical) — resolved by length alone.
      low: 70,
      median: 85,
      high: 95,
      sources: ['AdSights ThruPlay Rate glossary', 'Nielsen short-video completion 2024–2026'],
      note: 'Mechanical: short clips auto-clear the 15s/completion bar, so the rate reflects length, not creative quality — audience barely changes it.',
    },
    {
      id: '15-30s-cold',
      segment: '15–30s · cold prospecting (Reels/Stories)',
      length: '15–30s',
      audience: 'Cold prospecting',
      low: 18,
      median: 22,
      high: 28,
      sources: ['Affect Group 2025', 'AdSights 2026 ThruPlay benchmark guide'],
      note: 'The honest creative-quality band; below ~10% is usually a hook problem, 25%+ is strong.',
    },
    {
      id: '15-30s-warm',
      segment: '15–30s · warm / retargeting',
      length: '15–30s',
      audience: 'Warm / retargeting',
      low: 25,
      median: 32,
      high: 45,
      sources: ['AdSights ThruPlay Rate glossary'],
      note: 'Warm audiences clear the 15s threshold at materially higher rates — a high number here reflects audience temperature more than creative.',
    },
    {
      id: '30-60s-cold',
      segment: '30–60s · cold prospecting',
      length: '30–60s',
      audience: 'Cold prospecting',
      low: 8,
      median: 12,
      high: 18,
      sources: ['AdSights ThruPlay Rate glossary'],
      note: 'Longer creative loses roughly half of cold viewers before the 15s mark.',
    },
    {
      id: '30-60s-warm',
      segment: '30–60s · warm / retargeting',
      length: '30–60s',
      audience: 'Warm / retargeting',
      low: 12,
      median: 16,
      high: 25,
      directional: true,
      sources: ['AdSights ThruPlay Rate glossary (directional warm uplift)'],
      note: 'Directional — the 30–60s cold band lifted for warm audiences; no public 30–60s × warm range is cited.',
    },
  ],
}

/**
 * Resolves the ThruPlay segment for a (length, audience) pair. Under-15s is
 * audience-independent (mechanical), so it resolves by length alone.
 */
export function resolveThruplaySegment(
  length: string,
  audience: string,
): BenchmarkSegment | undefined {
  const segs = THRUPLAY_RATE_BENCHMARK.segments
  if (length === 'Under 15s') return segs.find((s) => s.length === 'Under 15s')
  return segs.find((s) => s.length === length && s.audience === audience)
}

/**
 * Hook / thumbstop rate (% of impressions that reach the platform's earliest
 * meaningful watch threshold). Higher is better. Thresholds differ by platform:
 * Meta operators typically use 3-second video views, TikTok uses 2-second video
 * views, and YouTube Shorts benchmarking is directional because Shorts metrics
 * vary by reporting surface.
 */
export const HOOK_RATE_BENCHMARK: BenchmarkSet = {
  id: 'hook-rate',
  metricLabel: 'Hook Rate',
  unit: 'percent',
  higherIsBetter: true,
  glossaryUrl: `${SITE}/resources/glossary/metrics/thumbstop-rate-tsr`,
  // No `referenceUrl`: BenchmarkComparison falls back to `glossaryUrl` (the
  // thumbstop-rate glossary) for "Full benchmarks & methodology". Pointing it
  // back at this tool page would make the methodology link self-referential.
  segments: [
    {
      id: 'meta-feed-cold',
      segment: 'Meta Feed · cold prospecting · 3-second views / impressions',
      audience: 'Cold prospecting',
      placement: 'Feed',
      low: 18,
      median: 23,
      high: 28,
      sources: ['Sepia Lab 2026 (Motion in-feed dataset)', 'Affect Group 2025', 'MHI 2026', 'AdSights 2026 benchmarks guide'],
      note: 'Meta Feed cold prospecting, 3-second video views ÷ impressions. Below ~15% is a hook problem; below ~18% usually signals a weak opener on cold traffic.',
    },
    {
      id: 'meta-reels-cold',
      segment: 'Meta Reels · cold prospecting · 3-second views / impressions',
      audience: 'Cold prospecting',
      placement: 'Reels',
      low: 24,
      median: 30,
      high: 36,
      sources: ['Sepia Lab 2026', 'adlibrary.com 2026', 'AdSights 2026 benchmarks guide'],
      note: 'Meta Reels cold prospecting. Full-screen autoplay runs hotter than Feed — compare Reels-only to Reels-only. (MHI 2026 reports Reels 5–10pp below Feed, so segment your own account before trusting either.)',
    },
    {
      id: 'meta-stories-cold',
      segment: 'Meta Stories · cold prospecting · 3-second views / impressions',
      audience: 'Cold prospecting',
      placement: 'Stories',
      low: 22,
      median: 27,
      high: 32,
      sources: ['Sepia Lab 2026', 'MHI Stories 2026', 'AdSights 2026 benchmarks guide'],
      note: 'Meta Stories cold prospecting. Full-screen helps, but skip behavior varies by creative format.',
    },
    {
      id: 'meta-warm',
      segment: 'Meta · warm / retargeting (any placement) · 3-second views / impressions',
      audience: 'Warm / retargeting',
      placement: 'Any',
      low: 30,
      median: 36,
      high: 45,
      directional: true,
      sources: ['Sepia Lab 2026', 'adlibrary.com 2026', 'AdSights 2026 benchmarks guide'],
      note: 'Warm/retargeting hook rate. Cited as a single placement-agnostic band (no public source splits warm hook by placement), so treat the placement breakdown as directional here. Brand recognition inflates hook rate — it is not a standalone creative-quality signal on warm audiences.',
    },
    {
      id: 'tiktok-2s',
      segment: 'TikTok · 2-second views / impressions',
      audience: 'TikTok',
      low: 35,
      median: 40,
      high: 45,
      sources: ['Billo 2026', 'Reloop 2026', 'TikTok Ads Manager metric definitions'],
      note: 'TikTok uses faster-scroll inventory, so the 2-second hook-rate benchmark is higher than Meta. Pair it with 6-second focused-view rate to separate hook strength from early hold.',
    },
    {
      id: 'youtube-shorts',
      segment: 'YouTube Shorts · engaged short-form view rate',
      audience: 'YouTube Shorts',
      low: 35,
      median: 45,
      high: 55,
      directional: true,
      sources: ['Store Growers 2026 YouTube Ads benchmarks', 'AdSights VCR/ThruPlay/VTR guide'],
      note: 'Directional: YouTube Shorts reporting is not identical to Meta/TikTok hook rate. Use this as a short-form attention check, not a strict cross-platform comparison.',
    },
  ],
}

/**
 * Resolves the hook-rate benchmark segment.
 *
 * Meta is placement-segmented (Feed/Reels/Stories × cold/warm), matching the
 * Video Creative Metrics Benchmarks 2026 guide; TikTok and YouTube Shorts are
 * single platform-level bands. The placement/audience args are optional and
 * only consulted for Meta — callers that pass just a platform (and any
 * non-Meta platform) get a sensible default, so existing one-arg call sites
 * keep working.
 *
 * @param platform - 'Meta' | 'TikTok' | 'YouTube Shorts'
 * @param opts.placement - Meta only: 'Feed' | 'Reels' | 'Stories'
 * @param opts.audience  - Meta only: 'Cold prospecting' | 'Warm / retargeting'
 */
export function resolveHookRateSegment(
  platform: string,
  opts?: { placement?: string; audience?: string },
): BenchmarkSegment | undefined {
  const segs = HOOK_RATE_BENCHMARK.segments
  if (platform === 'Meta') {
    const audience = opts?.audience ?? 'Cold prospecting'
    if (audience === 'Warm / retargeting') {
      return segs.find((s) => s.id === 'meta-warm')
    }
    const placement = opts?.placement ?? 'Feed'
    return (
      segs.find((s) => s.placement === placement && s.audience === 'Cold prospecting') ??
      segs.find((s) => s.id === 'meta-feed-cold')
    )
  }
  return segs.find((segment) => segment.audience === platform)
}

/**
 * Return on Ad Spend (revenue ÷ ad spend). Higher is better. Sources per
 * segment from the ROAS glossary `segmentBenchmarks`.
 */
export const ROAS_BENCHMARK: BenchmarkSet = {
  id: 'roas',
  metricLabel: 'ROAS',
  unit: 'ratio',
  higherIsBetter: true,
  glossaryUrl: `${SITE}/resources/glossary/metrics/return-on-ad-spend-roas`,
  segments: [
    {
      id: 'b2b-saas',
      segment: 'B2B SaaS',
      low: 3.0,
      median: 3.8,
      high: 5.0,
      sources: ['FirstPageSage 2025 Marketing ROI'],
      note: 'SaaS-calibrated (FirstPageSage) — non-SaaS B2B lead-gen (agency, local services, manufacturing) typically runs lower. Longer sales cycles depress immediate ROAS; pair with an LTV-adjusted view.',
    },
    {
      id: 'dtc-ecommerce',
      segment: 'Direct-to-Consumer e-commerce',
      low: 2.5,
      median: 3.0,
      high: 4.0,
      sources: ['WordStream Facebook Ads Benchmarks 2024', 'Common Thread Collective DTC Index'],
      note: 'High-margin verticals (beauty, supplements) can sustain 4.0x+.',
    },
  ],
}

/** All datasets, keyed by id, for lookup by tools. */
export const BENCHMARK_SETS = {
  [THRUPLAY_RATE_BENCHMARK.id]: THRUPLAY_RATE_BENCHMARK,
  [HOOK_RATE_BENCHMARK.id]: HOOK_RATE_BENCHMARK,
  [ROAS_BENCHMARK.id]: ROAS_BENCHMARK,
  // Cross-platform paid-social sets (CPM / CPC / CTR) live in ./paid-social —
  // same engine, same sourcing contract; registered here so every consumer
  // keeps one lookup surface.
  [PAID_SOCIAL_CPM_BENCHMARK.id]: PAID_SOCIAL_CPM_BENCHMARK,
  [PAID_SOCIAL_CPC_BENCHMARK.id]: PAID_SOCIAL_CPC_BENCHMARK,
  [PAID_SOCIAL_CTR_BENCHMARK.id]: PAID_SOCIAL_CTR_BENCHMARK,
} as const
